import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CalendarIcon, UserIcon, ArrowRightIcon } from 'lucide-react';
const blogPosts = [{
  id: 'eye-exams',
  title: 'The Importance of Comprehensive Eye Exams',
  excerpt: 'Regular eye exams are crucial not just for updating your prescription, but for detecting early signs of systemic health issues like diabetes and high blood pressure.',
  category: 'Eye Health',
  date: 'March 15, 2026',
  author: 'Dr. Nnabue',
  image: 'https://images.unsplash.com/photo-1574482620826-40685ca5ebd2?auto=format&fit=crop&q=80&w=800',
  readTime: '5 min read'
}, {
  id: 'retinal-screening',
  title: 'Why Retinal Screening is a Game Changer',
  excerpt: 'Advanced retinal imaging allows us to see the back of your eye in unprecedented detail, catching diseases like macular degeneration years before symptoms appear.',
  category: 'Technology',
  date: 'March 02, 2026',
  author: 'Dr. Adaeze Nnabue',
  image: 'https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?auto=format&fit=crop&q=80&w=800',
  readTime: '4 min read'
}, {
  id: 'contact-lenses',
  title: 'Finding the Perfect Contact Lenses for Your Lifestyle',
  excerpt: 'From daily disposables to multifocal lenses, the options can be overwhelming. Learn how we determine the best fit for your eyes and daily routine.',
  category: 'Eye Care',
  date: 'February 18, 2026',
  author: 'Dr. Nnabue',
  image: 'https://images.unsplash.com/photo-1512428559087-560fa5ceab42?auto=format&fit=crop&q=80&w=800',
  readTime: '6 min read'
}, {
  id: 'dry-eye-therapy',
  title: 'Finding Relief from Chronic Dry Eye',
  excerpt: 'If you suffer from gritty, burning, or excessively watery eyes, you might have Dry Eye Syndrome. Discover our personalized treatment approaches.',
  category: 'Wellness',
  date: 'February 05, 2026',
  author: 'Dr. Adaeze Nnabue',
  image: 'https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?auto=format&fit=crop&q=80&w=800',
  readTime: '5 min read'
}, {
  id: 'pediatric-eye-care',
  title: 'When Should Your Child Have Their First Eye Exam?',
  excerpt: "Vision problems can significantly impact a child's learning and development. Learn the milestones for pediatric eye care and what to watch out for.",
  category: 'Pediatric',
  date: 'January 22, 2026',
  author: 'Dr. Adaeze Nnabue',
  image: 'https://images.unsplash.com/photo-1591076482161-42ce6da69f67?auto=format&fit=crop&q=80&w=800',
  readTime: '4 min read'
}, {
  id: 'lasik-consultation',
  title: 'Is LASIK Right For You? What to Expect',
  excerpt: 'Considering laser vision correction? We break down the evaluation process, candidacy requirements, and what the recovery actually looks like.',
  category: 'Surgery',
  date: 'January 10, 2026',
  author: 'Dr. Nnabue',
  image: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?auto=format&fit=crop&q=80&w=800',
  readTime: '7 min read'
}];
const categories = ['All', ...Array.from(new Set(blogPosts.map(p => p.category)))];
export function BlogSection() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const filteredPosts = selectedCategory === 'All' ? blogPosts : blogPosts.filter(post => post.category === selectedCategory);
  return <section className="py-20 md:py-28 bg-surface-muted" data-id="element-90">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-91">
        <div className="text-center max-w-3xl mx-auto mb-10" data-id="element-92">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-section-mobile md:text-section text-brand-dark mb-4" data-id="element-93">
            Latest Insights & Eye Care Tips
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-subtitle text-neutral-text/80" data-id="element-94">
            Stay up to date with the latest news, trends, and expert advice for
            maintaining optimal eye health.
          </motion.p>
        </div>

        {/* Category Filters */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.15
      }} className="flex flex-wrap justify-center gap-3 mb-12" data-id="element-95">
          {categories.map(cat => <button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors focus-ring ${selectedCategory === cat ? 'bg-brand-dark text-white shadow-sm' : 'bg-white text-neutral-text/70 border border-gray-200 hover:border-brand-gold hover:text-brand-gold'}`} data-id="element-96">
              {cat}
              {cat !== 'All' && <span className="ml-1.5 text-xs opacity-60" data-id="element-97">
                  ({blogPosts.filter(p => p.category === cat).length})
                </span>}
            </button>)}
        </motion.div>

        <AnimatePresence mode="wait" data-id="element-98">
          <motion.div key={selectedCategory} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-99">
            {filteredPosts.map((post, index) => <motion.article key={post.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-card shadow-card hover:shadow-card-hover transition-all duration-300 border border-gray-100 overflow-hidden flex flex-col h-full group cursor-pointer" data-id="element-100">
                {/* Image Container */}
                <div className="relative h-56 overflow-hidden" data-id="element-101">
                  <div className="absolute inset-0 bg-brand-dark/10 group-hover:bg-transparent transition-colors duration-300 z-10" data-id="element-102" />
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" data-id="element-103" />
                  <div className="absolute top-4 left-4 z-20" data-id="element-104">
                    <span className="bg-brand-gold text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-sm" data-id="element-105">
                      {post.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 md:p-8 flex flex-col flex-grow" data-id="element-106">
                  <div className="flex items-center gap-4 text-sm text-neutral-text/60 mb-4 font-medium" data-id="element-107">
                    <div className="flex items-center gap-1.5" data-id="element-108">
                      <CalendarIcon className="w-3.5 h-3.5 text-brand-gold" data-id="element-109" />
                      {post.date}
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-110">
                      <UserIcon className="w-3.5 h-3.5 text-brand-gold" data-id="element-111" />
                      {post.author}
                    </div>
                  </div>

                  <h3 className="text-xl font-heading font-bold text-brand-dark mb-3 group-hover:text-brand-gold transition-colors leading-tight" data-id="element-112">
                    <Link to={`/blog/${post.id}`} className="focus-ring rounded-sm outline-none" data-id="element-113">
                      {post.title}
                    </Link>
                  </h3>

                  <p className="text-neutral-text/70 mb-6 text-base leading-relaxed flex-grow" data-id="element-114">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100" data-id="element-115">
                    <span className="text-sm text-neutral-text/50 font-medium" data-id="element-116">
                      {post.readTime}
                    </span>
                    <Link to={`/blog/${post.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-dark group-hover:text-brand-gold transition-colors focus-ring rounded-sm px-1 py-0.5" data-id="element-117">
                      Read Article
                      <ArrowRightIcon className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" data-id="element-118" />
                    </Link>
                  </div>
                </div>
              </motion.article>)}
          </motion.div>
        </AnimatePresence>

        <div className="mt-16 text-center" data-id="element-119">
          <button className="bg-white border-2 border-brand-dark text-brand-dark px-8 py-3.5 rounded-button font-heading font-semibold hover:bg-brand-dark hover:text-white transition-colors shadow-sm focus-ring" data-id="element-120">
            Load More Articles
          </button>
        </div>
      </div>
    </section>;
}